/**
 * Device-local store for pre-apply ("before") socket blobs.
 *
 * Deliberately a **separate IndexedDB database** from `bkSceneCache`, not a new
 * object store inside it: adding a store means bumping that DB's version, and a
 * failed upgrade there would take out scene resume — a much worse failure than
 * losing undo blobs. Separate databases fail independently.
 *
 * This is the *first* place a before-mesh lands. The upload to R2 happens after,
 * in the background, so Undo works immediately after an apply even while the
 * network request is still in flight (or has failed outright).
 *
 * A small synchronous index is mirrored into localStorage, matching the pattern
 * in `bkSceneCache`, because the Undo button's enabled state is decided during
 * render and cannot await IndexedDB.
 *
 * Every function is defensive: private mode, missing IDB and quota exhaustion
 * all degrade to "no local blob" rather than throwing into an apply or a render.
 */

const LOG_PREFIX = '[BeforeMeshCache]';

const DB_NAME = 'benx-undo-before';
const DB_VERSION = 1;
const STORE = 'blobs';
const INDEX_KEY = 'bk_undo_before_index_v1';

/** Roughly two full designs' worth of before-meshes before we start evicting. */
const SOFT_BUDGET_BYTES = 220 * 1024 * 1024;

/** @returns {string} IndexedDB primary key. */
function blobKey(orderKey, stepId, toolId) {
  return `${orderKey}::${stepId}::${toolId}`;
}

// ---------------------------------------------------------------------------
// Synchronous localStorage index
// ---------------------------------------------------------------------------

function readIndex() {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeIndex(index) {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(index));
  } catch (e) {
    console.warn(`${LOG_PREFIX} index write failed`, e);
  }
}

/** Synchronous: is there a local before-blob for this order/step/tool? */
export function hasLocalBeforeMesh(orderKey, stepId, toolId) {
  if (!orderKey || !stepId || !toolId) return false;
  return !!readIndex()[blobKey(orderKey, stepId, toolId)];
}

/** Synchronous: bytes held locally across all orders. */
export function localBeforeMeshBytes() {
  return Object.values(readIndex()).reduce(
    (sum, entry) => sum + (Number(entry?.bytes) || 0),
    0,
  );
}

function indexPut(key, meta) {
  const index = readIndex();
  index[key] = { ...meta, at: Date.now() };
  writeIndex(index);
}

function indexDelete(keys) {
  const index = readIndex();
  let changed = false;
  for (const key of keys) {
    if (index[key]) {
      delete index[key];
      changed = true;
    }
  }
  if (changed) writeIndex(index);
}

// ---------------------------------------------------------------------------
// IndexedDB
// ---------------------------------------------------------------------------

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    let request;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      console.warn(`${LOG_PREFIX} open failed`, e);
      return resolve(null);
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      console.warn(`${LOG_PREFIX} open error`, request.error);
      resolve(null);
    };
  });
  return dbPromise;
}

function withStore(mode, fn) {
  return openDb().then((db) => {
    if (!db) return null;
    return new Promise((resolve) => {
      let tx;
      try {
        tx = db.transaction(STORE, mode);
      } catch (e) {
        console.warn(`${LOG_PREFIX} transaction failed`, e);
        return resolve(null);
      }
      const store = tx.objectStore(STORE);
      let result = null;
      try {
        result = fn(store, (value) => {
          result = value;
        });
      } catch (e) {
        console.warn(`${LOG_PREFIX} store op threw`, e);
      }
      tx.oncomplete = () => resolve(result);
      tx.onabort = () => {
        console.warn(`${LOG_PREFIX} transaction aborted`, tx.error);
        resolve(null);
      };
      tx.onerror = () => resolve(null);
    });
  });
}

/**
 * Store a packed before-blob.
 *
 * @param {string} orderKey
 * @param {string} stepId
 * @param {string} toolId
 * @param {ArrayBuffer} buffer
 * @param {{ vertexCount?: number }} [meta]
 * @returns {Promise<boolean>}
 */
export async function putBeforeMesh(orderKey, stepId, toolId, buffer, meta = {}) {
  if (!orderKey || !stepId || !toolId || !(buffer instanceof ArrayBuffer)) return false;

  if (localBeforeMeshBytes() + buffer.byteLength > SOFT_BUDGET_BYTES) {
    await evictOldest(buffer.byteLength);
  }

  const key = blobKey(orderKey, stepId, toolId);
  const record = {
    key,
    orderKey,
    stepId,
    toolId,
    buffer,
    bytes: buffer.byteLength,
    vertexCount: meta.vertexCount ?? null,
    createdAt: Date.now(),
  };

  const ok = await withStore('readwrite', (store) => {
    store.put(record);
    return true;
  });

  if (ok) {
    indexPut(key, {
      orderKey,
      stepId,
      toolId,
      bytes: buffer.byteLength,
      vertexCount: record.vertexCount,
    });
    return true;
  }
  console.warn(`${LOG_PREFIX} local write failed for ${key} — undo will rely on the server copy`);
  return false;
}

/**
 * @param {string} orderKey
 * @param {string} stepId
 * @param {string} toolId
 * @returns {Promise<ArrayBuffer | null>}
 */
export function getBeforeMesh(orderKey, stepId, toolId) {
  if (!orderKey || !stepId || !toolId) return Promise.resolve(null);
  const key = blobKey(orderKey, stepId, toolId);
  return withStore('readonly', (store, done) => {
    const req = store.get(key);
    req.onsuccess = () => done(req.result?.buffer ?? null);
    req.onerror = () => done(null);
    return null;
  }).then((buffer) => buffer ?? null);
}

/** @returns {Promise<boolean>} */
export async function deleteBeforeMesh(orderKey, stepId, toolId) {
  const key = blobKey(orderKey, stepId, toolId);
  const ok = await withStore('readwrite', (store) => {
    store.delete(key);
    return true;
  });
  indexDelete([key]);
  return !!ok;
}

/**
 * Drop every blob for these steps of one order. Used when a backward revert
 * discards the work on later steps.
 *
 * @param {string} orderKey
 * @param {string[]} stepIds
 * @returns {Promise<number>} blobs deleted
 */
export async function deleteBeforeMeshesForSteps(orderKey, stepIds) {
  if (!orderKey || !stepIds?.length) return 0;
  const targets = new Set(stepIds);
  const index = readIndex();
  const keys = Object.entries(index)
    .filter(([, e]) => e?.orderKey === orderKey && targets.has(e?.stepId))
    .map(([key]) => key);
  if (keys.length === 0) return 0;

  await withStore('readwrite', (store) => {
    for (const key of keys) store.delete(key);
    return true;
  });
  indexDelete(keys);
  return keys.length;
}

/**
 * Keep only blobs for `stepIds`; drop the rest of the order. Post-export pruning.
 *
 * @param {string} orderKey
 * @param {string[]} stepIds
 * @returns {Promise<number>} blobs deleted
 */
export async function retainBeforeMeshesForSteps(orderKey, stepIds) {
  if (!orderKey) return 0;
  const keep = new Set(stepIds ?? []);
  const index = readIndex();
  const keys = Object.entries(index)
    .filter(([, e]) => e?.orderKey === orderKey && !keep.has(e?.stepId))
    .map(([key]) => key);
  if (keys.length === 0) return 0;

  await withStore('readwrite', (store) => {
    for (const key of keys) store.delete(key);
    return true;
  });
  indexDelete(keys);
  return keys.length;
}

/** @returns {Promise<number>} blobs deleted */
export async function deleteAllBeforeMeshesForOrder(orderKey) {
  return retainBeforeMeshesForSteps(orderKey, []);
}

/**
 * Free at least `neededBytes` by dropping the oldest blobs across all orders.
 * Oldest-first rather than LRU: an old design's undo material is the least likely
 * to be wanted, and we do not track reads.
 */
async function evictOldest(neededBytes) {
  const index = readIndex();
  const entries = Object.entries(index).sort((a, b) => (a[1]?.at ?? 0) - (b[1]?.at ?? 0));
  const doomed = [];
  let freed = 0;
  for (const [key, meta] of entries) {
    doomed.push(key);
    freed += Number(meta?.bytes) || 0;
    if (freed >= neededBytes) break;
  }
  if (doomed.length === 0) return;

  await withStore('readwrite', (store) => {
    for (const key of doomed) store.delete(key);
    return true;
  });
  indexDelete(doomed);
  console.log(`${LOG_PREFIX} evicted ${doomed.length} blob(s) to free ${freed} bytes`);
}

/** Local blob inventory for an order — diagnostics and the pruning report. */
export function listLocalBeforeMeshes(orderKey) {
  return Object.entries(readIndex())
    .filter(([, e]) => !orderKey || e?.orderKey === orderKey)
    .map(([key, e]) => ({ key, ...e }));
}
